/**
 * Botão + popover: outros equipamentos em que o compatível está vinculado.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCirclesRelation } from '@tabler/icons-react';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { fetchCompatEquipmentLinksByProduct } from './compatEquipApi';
import './CompatLinkedModelsPopover.css';

/** ~60 chars de nome de equipamento em fonte 12.5px + meta */
const POP_W = 460;
const GAP = 8;

function placeNear(anchor) {
  if (!anchor || typeof window === 'undefined') return null;
  const r = anchor.getBoundingClientRect();
  const maxH = Math.min(320, window.innerHeight - 24);
  let left = Math.min(Math.max(8, r.right - POP_W), window.innerWidth - POP_W - 8);
  let top = r.bottom + GAP;
  let place = 'below';
  if (top + Math.min(220, maxH) > window.innerHeight - 8) {
    top = Math.max(8, r.top - GAP - Math.min(220, maxH));
    place = 'above';
  }
  return { top, left, width: POP_W, maxHeight: maxH, place };
}

export default function CompatLinkedModelsPopover({
  productCode,
  productName,
  excludeEquipmentCode,
  disabled,
}) {
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  const excludeCode = String(excludeEquipmentCode || '').trim();

  const visibleItems = useMemo(() => {
    if (!excludeCode) return items;
    return items.filter((it) => String(it.equipmentCode || '').trim() !== excludeCode);
  }, [items, excludeCode]);

  const reposition = useCallback(() => {
    if (!btnRef.current) return;
    setPos(placeNear(btnRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reposition();
    return undefined;
  }, [open, reposition]);

  useEffect(() => {
    if (!open || !productCode) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setItems([]);
    fetchCompatEquipmentLinksByProduct(productCode)
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Falha ao carregar modelos');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, productCode]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (btnRef.current?.contains(ev.target) || popRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    const onRepos = () => reposition();
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onRepos);
    window.addEventListener('scroll', onRepos, true);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onRepos);
      window.removeEventListener('scroll', onRepos, true);
    };
  }, [open, reposition]);

  const button = (
    <button
      ref={btnRef}
      type="button"
      className={'vp-mock-linked-models' + (open ? ' is-open' : '')}
      aria-label="Modelos compatíveis"
      aria-expanded={open}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        setOpen((v) => !v);
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <IconCirclesRelation size={16} stroke={1.75} aria-hidden />
    </button>
  );

  return (
    <>
      {open ? (
        button
      ) : (
        <GoHoverTooltip text="Modelos compatíveis" align="center" delayMs={160}>
          {button}
        </GoHoverTooltip>
      )}
      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              className={`vp-mock-linked-models-pop vp-mock-linked-models-pop--${pos.place}`}
              style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
              role="dialog"
              aria-label="Modelos compatíveis"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="vp-mock-linked-models-pop-head">
                <IconCirclesRelation size={15} stroke={1.75} aria-hidden />
                <span className="vp-mock-linked-models-pop-title">Modelos compatíveis</span>
              </div>
              {productName ? (
                <div className="vp-mock-linked-models-pop-sub">{productName}</div>
              ) : null}
              <div className="vp-mock-linked-models-pop-body">
                {loading ? (
                  <div className="vp-mock-linked-models-empty">Carregando…</div>
                ) : error ? (
                  <div className="vp-mock-linked-models-empty is-error">{error}</div>
                ) : visibleItems.length === 0 ? (
                  <div className="vp-mock-linked-models-empty">
                    Nenhum outro equipamento vinculado a este compatível.
                  </div>
                ) : (
                  <ul className="vp-mock-linked-models-list">
                    {visibleItems.map((it) => (
                      <li key={it.equipmentCode} className="vp-mock-linked-models-item">
                        <span className="vp-mock-linked-models-name">{it.equipmentName}</span>
                        <span className="vp-mock-linked-models-meta">
                          {it.equipmentCode}
                          {it.brandName && it.brandName !== '—' ? ` · ${it.brandName}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
